import { CarPdfViewer } from "@app/components/PdfViewer";
import { CarPdfDocument } from "@app/components/PdfDocument";
import { CarPdfPage, defaultClientPageSize } from "@app/components/PdfPage";
import { CarPdfTypography } from "@app/components/PdfTypography";
import { useUser } from "@app/pages/settings/useUser";
import { OutUnderPerformancePdfChart } from "@app/pages/assetClassAnalysis/pdfPerformanceRiskReport/OutUnderPerformancePdfChart";
import { usePortfolioGroupInfo } from "@app/pages/assetAllocation/usePortfolioGroupInfo";
import { usePortfolioGroupVersionStatData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatData";

export const OUT_UNDER_PERFORMANCE_CHART_HEIGHT = 176;

interface ModulePerformanceRiskReportProps {
  portfolioGroupId: string;
  moduleId: string;
  data1Id: string;
  data2Id: string;
  startYear: number;
  endYear: number;
  footer: string;
}

export const ModulePerformanceRiskReport = ({
  portfolioGroupId,
  moduleId,
  data1Id,
  data2Id,
  startYear,
  endYear,
  footer,
}: ModulePerformanceRiskReportProps) => {
  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const { portfolioGroupVersionId, publishedVersionId, isSinglePortfolioMode } =
    groupInfo;

  const statData = usePortfolioGroupVersionStatData({
    versionId: portfolioGroupVersionId,
    publishedVersionId,
    filterByModuleId: moduleId,
  });

  const user = useUser();

  const moduleName = statData.getModuleNameById(moduleId);

  const title = `Performance Risk - ${isSinglePortfolioMode ? groupInfo.title : moduleName}`;

  return (
    <CarPdfViewer
      isLoading={user.isLoading || groupInfo.isLoading || statData.isLoading}
    >
      <CarPdfDocument title={title} author={user.userFullName}>
        {
          <CarPdfPage
            sectionTitle="Analyze"
            reportTitle={title}
            footer={footer}
          >
            <CarPdfTypography
              variant="v14Semibold"
              dense
              style={{ marginTop: 16 }}
            >
              {`1 Year Underperformance Risk: ${startYear} - ${endYear}`}
            </CarPdfTypography>
            <OutUnderPerformancePdfChart
              style={{ marginTop: 14 }}
              width={defaultClientPageSize.width}
              height={OUT_UNDER_PERFORMANCE_CHART_HEIGHT}
              data={statData.oneYearUnderPerf}
              periods={statData.periods}
              data1Id={data1Id}
              data2Id={data2Id}
              startYear={startYear}
              endYear={endYear}
              isRelative={false}
              isWhiteContext
            />
            <CarPdfTypography
              variant="v14Semibold"
              dense
              style={{ marginTop: 50 }}
            >
              {`3 Year Underperformance Risk: ${startYear} - ${endYear}`}
            </CarPdfTypography>
            <OutUnderPerformancePdfChart
              style={{ marginTop: 14 }}
              width={defaultClientPageSize.width}
              height={OUT_UNDER_PERFORMANCE_CHART_HEIGHT}
              data={statData.threeYearUnderPerf}
              periods={statData.periods}
              data1Id={data1Id}
              data2Id={data2Id}
              startYear={startYear}
              endYear={endYear}
              isRelative={false}
              isWhiteContext
            />
            <CarPdfTypography
              break
              variant="v14Semibold"
              dense
              style={{ marginTop: 8 }}
            >
              {`Cumulative Relative Performance: ${startYear} - ${endYear}`}
            </CarPdfTypography>
            <OutUnderPerformancePdfChart
              style={{ marginTop: 14 }}
              width={defaultClientPageSize.width}
              height={OUT_UNDER_PERFORMANCE_CHART_HEIGHT}
              data={statData.cumRelPerf}
              periods={statData.periods}
              data1Id={data1Id}
              data2Id={data2Id}
              startYear={startYear}
              endYear={endYear}
              isRelative={true}
              isWhiteContext
            />
          </CarPdfPage>
        }
      </CarPdfDocument>
    </CarPdfViewer>
  );
};
